import { useCallback, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { resolveS2Id, postBatches, deduplicatePapers } from "@/lib/trawl-utils";
import { fetchPaperNetwork } from "@/lib/trawl-utils";
import { S2AuthError } from "@/services/s2-client";
import { useSnowballStore } from "@/stores/snowball-store";
import { postRunTiming, toastJobFinished, type RunOutcome } from "@/lib/runTiming";
import { cachedPapers, openPageRun } from "@/lib/pageRun";
import { remainingSeeds } from "@/lib/jobRuns";
import { toast } from "@/lib/toast";
import { useActivityStore } from "@/stores/activity-store";
import type { JobRun } from "@/hooks/useActivity";
import type { PaperWithRole } from "@/types/paper";

/**
 * Above this many newly-discovered papers, ask the user to confirm before
 * committing them — a large snowball pull grows the corpus a lot, costs scoring,
 * and can dilute the score distribution. See beta feedback (1064-paper pull).
 */
export const LARGE_SNOWBALL_THRESHOLD = 200;

/**
 * Filter papers eligible as snowball seeds:
 * - normalized_score >= threshold
 * - has paper_id or doi (so we can resolve S2 ID)
 */
export function getEligibleSeeds(
  papers: PaperWithRole[],
  threshold: number,
): PaperWithRole[] {
  return papers.filter(
    (p) =>
      p.normalized_score >= threshold &&
      (p.paper_id || p.doi),
  );
}

export function useSnowball(conceptId: string) {
  const queryClient = useQueryClient();

  const startSnowball = useCallback(
    async (papers: PaperWithRole[], opts: { resume?: JobRun } = {}) => {
      const {
        tryStartSnowball,
        updateProgress,
        completeSnowball,
        failSnowball,
        options,
      } = useSnowballStore.getState();

      // Resume: exactly the seeds the stopped run hadn't finished.
      const left = opts.resume ? new Set(remainingSeeds(opts.resume)) : null;
      const seeds = left
        ? papers.filter((p) => left.has(p.id))
        : getEligibleSeeds(papers, options.threshold);
      if (seeds.length === 0) return;

      // Re-entry guard: Resume from the panel bypasses Curate's busy gate.
      const controller = tryStartSnowball();
      if (!controller) {
        toast.info("A snowball is already running");
        return;
      }
      const signal = controller.signal;
      let startedAt = Date.now();
      let outcome: RunOutcome = "cancelled";
      let endText: string | undefined;
      // Durable jobs: each seed's new papers are saved as it finishes and the
      // run records which seeds are done, so a closed page can Resume.
      const run = await openPageRun(
        queryClient, conceptId, "snowball", seeds.map((s) => s.id), opts.resume,
      );
      const saved = { added: 0, duplicates: 0 };
      let confirmedLarge = false;

      // Build a set of existing paper IDs for dedup
      const corpusIds = new Set(
        papers
          .map((p) => p.paper_id)
          .filter((id): id is string => id != null),
      );

      let succeeded = 0;
      let failed = 0;
      const allNewPapers: ReturnType<typeof deduplicatePapers> = [];
      // Distinct papers discovered this run (for the progress count).
      const runIds = new Set<string>();
      let runNoIdCount = 0;

      try {
        updateProgress({
          total: seeds.length,
          statusMessage: `Snowball: processing ${seeds.length} seed papers...`,
        });

        for (let i = 0; i < seeds.length; i++) {
          if (signal.aborted) break;

          const seed = seeds[i];
          updateProgress({
            completed: i,
            currentSeed: seed.title,
            percent: Math.round((i / seeds.length) * 90),
            statusMessage: `Processing seed ${i + 1} of ${seeds.length}...`,
          });

          try {
            const s2Id = await resolveS2Id(seed, signal);
            if (!s2Id) {
              failed++;
              continue;
            }

            const discovered = await fetchPaperNetwork(s2Id, {
              maxResults: 100,
              includeReferences: options.includeReferences,
              includeRecommendations: false,
              signal,
              parent: { paperId: seed.id, method: "snowball" },
            });

            // Filter out papers already in the corpus. Papers an earlier seed
            // in this run already found are KEPT so deduplicatePapers can
            // union their discovered_via links (no provenance loss).
            const novel = discovered.filter(
              (p) => !p.paper_id || !corpusIds.has(p.paper_id),
            );

            allNewPapers.push(...novel);

            for (const p of novel) {
              if (p.paper_id) runIds.add(p.paper_id);
              else runNoIdCount++;
            }

            if (run) {
              const unique = deduplicatePapers(novel);
              // The large-pull heads-up, asked when the run's distinct new
              // papers first cross the line (seeds save as they go).
              const found = runIds.size + runNoIdCount;
              if (!confirmedLarge && found >= LARGE_SNOWBALL_THRESHOLD) {
                const askedAt = Date.now();
                const proceed = window.confirm(
                  `Snowball has found ${found} new papers so far (${seeds.length - i - 1} seeds to go).\n\n` +
                    `Adding them will grow your corpus, and each will be scored against this ` +
                    `concept (this takes time and can shift your relative scores).\n\nKeep going?`,
                );
                startedAt += Date.now() - askedAt;
                confirmedLarge = true;
                if (!proceed) {
                  endText = `Stopped at ${found} papers`;
                  completeSnowball(
                    `Snowball stopped — ${saved.added} papers from earlier seeds were added; this seed's ${unique.length} were not.`,
                  );
                  return;
                }
              }
              if (unique.length > 0) {
                const r = await postBatches(conceptId, unique, signal, "snowball");
                saved.added += r.added;
                saved.duplicates += r.duplicates;
              }
              run.seedDone(seed.id);
            }

            succeeded++;
          } catch (err) {
            if (signal.aborted) break;
            // Signed out: every seed would fail the same way.
            if (err instanceof S2AuthError) throw err;
            failed++;
          }

          updateProgress({
            succeeded,
            failed,
            discoveredCount: runIds.size + runNoIdCount,
          });
        }

        if (signal.aborted) return;

        // Deduplicate across all seeds
        updateProgress({
          statusMessage: "Deduplicating and saving...",
          percent: 92,
        });
        const unique = deduplicatePapers(allNewPapers);

        // Heads-up before a large pull — the count is only known now, after
        // discovery + dedup. window.confirm matches the app's existing
        // confirmation convention (FilterBar, HighlightsSidebar, etc.).
        if (!run && unique.length >= LARGE_SNOWBALL_THRESHOLD) {
          const askedAt = Date.now();
          const proceed = window.confirm(
            `Snowball found ${unique.length} new papers.\n\n` +
              `Adding them all will grow your corpus by ${unique.length} papers, ` +
              `and each will be scored against this concept (this takes time and ` +
              `can shift your relative scores).\n\nAdd all ${unique.length} papers?`,
          );
          // Time spent reading the dialog isn't run time.
          startedAt += Date.now() - askedAt;
          if (!proceed) {
            completeSnowball(
              `Snowball cancelled — ${unique.length} discovered papers were not added.`,
            );
            return;
          }
        }

        if (unique.length > 0) {
          updateProgress({
            statusMessage: `Saving ${unique.length} new papers...`,
            percent: 95,
          });
          // With a run, each seed's papers were saved as it finished.
          const result = run ? saved : await postBatches(conceptId, unique, signal, "snowball");

          await queryClient.invalidateQueries({
            queryKey: ["papers", conceptId],
          });
          await queryClient.invalidateQueries({ queryKey: ["concepts"] });

          const parts = [
            `${result.added} new papers from ${succeeded} seeds`,
            result.duplicates > 0
              ? `${result.duplicates} duplicates skipped`
              : "",
            failed > 0 ? `${failed} seeds failed` : "",
          ].filter(Boolean);

          completeSnowball(parts.join(", "));
          outcome = "success";
          endText = `${result.added} new papers`;
          toastJobFinished(conceptId, "Snowball finished", parts.join(", "));
        } else {
          const message = `No new papers found from ${succeeded} seeds.${failed > 0 ? ` ${failed} seeds failed.` : ""}`;
          completeSnowball(message);
          outcome = "success";
          endText = "No new papers";
          toastJobFinished(conceptId, "Snowball finished", message);
        }
      } catch (err) {
        if (!signal.aborted) {
          outcome = "failed";
          endText = err instanceof Error ? err.message : "Snowball failed";
          failSnowball(endText);
        }
      } finally {
        postRunTiming(conceptId, "snowball", seeds.length, startedAt, outcome);
        run?.end(outcome === "success" ? "done" : outcome, endText);
        // Saved per seed: show them even when the run stopped early.
        if (run && saved.added > 0 && outcome !== "success") {
          void queryClient.invalidateQueries({ queryKey: ["papers", conceptId] });
          void queryClient.invalidateQueries({ queryKey: ["paper-keys", conceptId] });
          void queryClient.invalidateQueries({ queryKey: ["concepts"] });
        }
      }
    },
    [conceptId, queryClient],
  );

  // Resume a snowball the page stopped (Activity panel).
  useEffect(() => {
    const resume = (run: JobRun) => {
      const papers = cachedPapers(queryClient, conceptId);
      const left = new Set(remainingSeeds(run));
      if (!papers.some((p) => left.has(p.id))) {
        toast.info("Those seeds are no longer in this concept");
        return;
      }
      void startSnowball(papers, { resume: run });
    };
    const { registerResumer } = useActivityStore.getState();
    registerResumer("snowball", resume);
    return () => registerResumer("snowball", null);
  }, [startSnowball, queryClient, conceptId]);

  const cancelSnowball = useCallback(() => {
    useSnowballStore.getState().cancelSnowball();
  }, []);

  return { startSnowball, cancelSnowball };
}
